import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MSB_AGENT_FORM_URL,
  MSB_AGENT_POSTER_URL,
  MSB_AGENT_VIDEO_URL,
  onMsbAgentOpen,
} from "../../utils/msb-agent";

const SPRITE = "/img/new-icons/msb-agent-sprite.svg";

const FEATURES = [
  { key: "feature1", icon: "i-checkins" },
  { key: "feature2", icon: "i-chat" },
  { key: "feature3", icon: "i-programs" },
  { key: "feature4", icon: "i-export" },
  { key: "feature5", icon: "i-exercise" },
];

const Icon = ({ name, small = false }: { name: string; small?: boolean }) => (
  <svg className={small ? "i sm" : "i"} aria-hidden="true">
    <use href={`${SPRITE}#${name}`} />
  </svg>
);

export const MsbAgentModal = ({ activeUser }) => {
  const { t } = useTranslation("coaching-portal");
  const isCoach = !!activeUser?.permissions?.includes("coach");
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "done" | "popupBlocked">(
    "idle"
  );
  const [videoFailed, setVideoFailed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(
    () =>
      onMsbAgentOpen((trigger) => {
        triggerRef.current = trigger;
        setStatus("idle");
        setVideoFailed(false);
        setIsOpen(true);
      }),
    []
  );

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, close]);

  if (!isCoach || !isOpen) return null;

  const playWithSound = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = false;
    video.currentTime = 0;
    // The footer button stays visible while the modal body is scrolled away from the video.
    video
      .closest(".player")
      ?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    video.play().catch(() => setVideoFailed(true));
  };

  const retryVideo = () => {
    const video = videoRef.current;
    setVideoFailed(false);
    if (!video) return;
    video.load();
    video.play().catch(() => {});
  };

  // window.open must run synchronously in the click handler or pop-up blockers drop it.
  // No "noopener" feature: it makes open() return null even on success, which
  // would read as a blocked pop-up. Detach the opener by hand instead.
  const requestAccess = () => {
    const opened = window.open(MSB_AGENT_FORM_URL, "_blank");
    if (opened) opened.opener = null;
    setStatus(opened ? "done" : "popupBlocked");
  };

  const firstName =
    activeUser?.firstName || String(activeUser?.name || "").split(" ")[0];

  return (
    <div className="Modal-Box msb-agent-modal">
      <button
        type="button"
        className="overlay"
        aria-label={t("msbAgent.close")}
        onClick={close}
      />
      <div
        className="box"
        role="dialog"
        aria-modal="true"
        aria-labelledby="msb-agent-title"
      >
        {status === "done" ? (
          <div className="done" aria-live="polite">
            <button
              type="button"
              className="close"
              aria-label={t("msbAgent.close")}
              onClick={close}
            >
              <img src="/img/new-icons/close-modal.svg" alt="" />
            </button>
            <div className="check-ring">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12.5l4.5 4.5L19 7.5" />
              </svg>
            </div>
            <h2 id="msb-agent-title">{t("msbAgent.doneTitle")}</h2>
            <p className="desc">
              {t("msbAgent.doneBody", { name: firstName })}
            </p>
            <div className="steps">
              {[
                { number: 1, tone: "success" },
                { number: 2, tone: "info" },
                { number: 3, tone: "neutral" },
              ].map(({ number, tone }) => (
                <div className="step" key={number}>
                  <div className="eyebrow">
                    {t("msbAgent.step", { number })}
                    <span className={`pill ${tone}`}>
                      {t(`msbAgent.step${number}Status`)}
                    </span>
                  </div>
                  <div className="t">{t(`msbAgent.step${number}Title`)}</div>
                  <div className="s">{t(`msbAgent.step${number}Body`)}</div>
                </div>
              ))}
            </div>
            <div className="done-foot">
              <span>
                {t("msbAgent.doneEmail", { email: activeUser?.email })}
              </span>
              <button
                type="button"
                data-button-type="primary"
                onClick={close}
                ref={closeRef}
              >
                {t("msbAgent.backToClients")}
              </button>
            </div>
          </div>
        ) : (
          <div className="main">
            <div className="modal-head">
              <div className="grow">
                <span className="pill success">
                  {t("msbAgent.privateBeta")}
                </span>
                <h2 id="msb-agent-title">
                  <img src="/img/new-icons/msb-logo-mark.svg" alt="" />
                  {t("msbAgent.title")}
                </h2>
                <p className="desc">{t("msbAgent.subtitle")}</p>
              </div>
              <button
                type="button"
                className="close"
                aria-label={t("msbAgent.close")}
                onClick={close}
                ref={closeRef}
              >
                <img src="/img/new-icons/close-modal.svg" alt="" />
              </button>
            </div>
            <div className="modal-body">
              <div className={`player${videoFailed ? " failed" : ""}`}>
                <video
                  ref={videoRef}
                  src={MSB_AGENT_VIDEO_URL}
                  poster={MSB_AGENT_POSTER_URL}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  onError={() => setVideoFailed(true)}
                />
                <div className="vid-err" role="alert">
                  <Icon name="i-info" />
                  <b>{t("msbAgent.videoFailedTitle")}</b>
                  <span>{t("msbAgent.videoFailedBody")}</span>
                  <button
                    type="button"
                    className="vid-retry"
                    data-button-type="secondary"
                    onClick={retryVideo}
                  >
                    {t("msbAgent.tryAgain")}
                  </button>
                </div>
              </div>
              <div>
                <div className="section-label eyebrow">
                  {t("msbAgent.whatItDoes")}
                </div>
                <div className="feat-panel">
                  {FEATURES.map(({ key, icon }) => (
                    <div className="feat" key={key}>
                      <span className="ic">
                        <Icon name={icon} />
                      </span>
                      <p>
                        <b>{t(`msbAgent.${key}Title`)}</b>{" "}
                        {t(`msbAgent.${key}Body`)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="trust">
                <Icon name="i-access" />
                <div>
                  <p>{t("msbAgent.trust")}</p>
                  <p className="easy">{t("msbAgent.trustSub")}</p>
                </div>
              </div>
            </div>
            {status === "popupBlocked" && (
              <div className="field-msg foot-err" role="alert">
                <Icon name="i-info" small />
                {t("msbAgent.popupBlocked")}
              </div>
            )}
            <div className="modal-foot">
              <p className="fine">{t("msbAgent.footerNote")}</p>
              <button
                type="button"
                data-button-type="secondary"
                onClick={playWithSound}
              >
                {t("msbAgent.watchVideo")}
              </button>
              <button
                type="button"
                data-button-type="primary"
                onClick={requestAccess}
              >
                {t("msbAgent.requestAccess")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
